import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type maplibregl from 'maplibre-gl';

import { readAggregateClusterSummary } from '@/components/map/utils/entityClusterProperties';
import { ORBIT_LAYER_IDS } from '@/components/map/utils/mapLayerOrder';
import { useClusterCardHover } from '@/hooks/useClusterCardHover';
import type { ClusterCardHit, ClusterCardHoverOptions, ClusterCardHoverState } from '@/hooks/useClusterCardHover';
import { POI_MARKER_MIN_ZOOM } from '@/hooks/useEntitiesLayer';
import { EXPLORER_CELLS_SOURCE_ID } from '@/hooks/useNetworkExplorerClustersLayer';

export type NetworkExplorerClusterInteractionState = ClusterCardHoverState;

// A click zooms in, as on the single-client bubbles: a cluster eases to the zoom at which it
// splits, a lone cell straight to the marker handover, where the individual markers take over.
function zoomIntoBubble(hit: ClusterCardHit, mlMap: maplibregl.Map) {
    const clusterId = hit.feature.properties['cluster_id'] as number | undefined;
    if (clusterId == null) {
        mlMap.easeTo({ center: hit.coords, zoom: POI_MARKER_MIN_ZOOM });
        return;
    }
    const source = mlMap.getSource<maplibregl.GeoJSONSource>(EXPLORER_CELLS_SOURCE_ID);
    if (!source) return;
    void source
        .getClusterExpansionZoom(clusterId)
        .then((zoom) => {
            mlMap.easeTo({ center: hit.coords, zoom });
        })
        .catch(() => {
            // The cluster regrouped under the click; there is nothing left to expand.
        });
}

const EXPLORER_BUBBLE_OPTIONS: ClusterCardHoverOptions = {
    // The circle layer only, as for the single-client clusters. The count label sits on top
    // of the circle and MapLibre's layer-scoped hit test still finds the circle underneath
    // it, so the label needs no listeners of its own — and must not have any: a listener on
    // the label fires a "leave" every time the cursor crosses the label's edge inside the
    // bubble, which would start the dismissal timer while the cursor is still on the bubble.
    layerIds: [ORBIT_LAYER_IDS.explorerClusters],
    // Every feature on the explorer source is an aggregate, so a lone cell shows a card too.
    // Cluster ids are unique within the one source; a lone cell has none, so its anchor
    // stands in.
    keyFor: (feature, coords) => {
        const clusterId = feature.properties['cluster_id'] as number | undefined;
        return clusterId != null ? `cluster:${String(clusterId)}` : `cell:${String(coords[0])},${String(coords[1])}`;
    },
    readSummary: readAggregateClusterSummary,
    onClick: zoomIntoBubble,
};

/**
 * The Network Explorer bubbles' hover card and click, behaving exactly like the single-client
 * cluster bubbles (the designer's call, so the two maps read as one product): hovering shows
 * the breakdown card, clicking zooms in. The shared hover machinery lives in
 * useClusterCardHover; this hook supplies the explorer's layer, keys, summary reader and zoom.
 *
 * Nothing here reads or changes the selected client — the card describes activity across
 * whichever networks the bubble spans.
 */
export function useNetworkExplorerClusterInteractions(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
    enabled: boolean,
): NetworkExplorerClusterInteractionState {
    return useClusterCardHover(mapRef, isMapLoaded, enabled, EXPLORER_BUBBLE_OPTIONS);
}
